import invariant from 'invariant';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { ApiFileUploadTypes } from 'api/types/ApiFileUploadTypes';
import { Box } from 'components/Box';
import { configValue } from 'config/appConfigUtils';
import { useUserEnvironment } from 'modules/main/store/hooks/useUserEnvironment';
import { getUserType } from 'utils/analytics/helpers/getUserType';
import { trackEvent } from 'utils/analytics/track/trackEvent';
import { waitTaskCompleted } from 'utils/asyncBackendTask';
import { partial } from 'utils/functional';
import { getJson, postData, postJson } from 'utils/http';
import { fileIdToUrl } from 'utils/s3file';

const InputContainer = styled(Box)`
  input {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
    &[disabled] {
      cursor: not-allowed;
    }
  }
`;

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const objToFormData = (o: Record<string, any>): FormData => {
  const f = new FormData();
  Object.entries(o).forEach(([k, v]) => f.append(k, v));
  return f;
};

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const uploadPresignedUrl = (f: File, r: Record<string, any>) => {
  const { params, s3File } = r;
  const formData = objToFormData(params.fields);
  formData.append('file', f);
  return postData(params.url, formData).then(() =>
    // @ts-expect-error TS(2345): Argument of type '({ task }: { task: any; }) => Pr... Remove this comment to see the full error message
    postJson(`/data/s3-uploads/${s3File.id}/done`).then(({ task }) =>
      waitTaskCompleted(task).then(() => s3File.id),
    ),
  );
};

const useMock = configValue('idealist', 'fileUploadMock');

const uploadToS3 = (f: File, fileType: ApiFileUploadTypes) =>
  postJson('/data/s3-uploads', {
    fileType,
    filename: f.name,
    size: f.size,
    // bytes
    mimeType: f.type,
  }).then(partial(uploadPresignedUrl, f));

type Props = {
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children: (arg0: Record<string, any>) => ReactNode;
  downloadAttachment?: boolean;
  name: string;
  isRequired: boolean;
  qaId: string;
  fileId: string | undefined;
  fileType: ApiFileUploadTypes;
  onChange: (s3FileId: string) => void;
  onError?: (arg0: Error) => void;
  accept?: string;
  'aria-label'?: string;
  disabled?: boolean;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  extraAnalyticsData?: Record<string, any>;
};

export function S3Picker({
  children,
  name,
  downloadAttachment,
  isRequired,
  qaId,
  fileId,
  fileType,
  onChange,
  onError,
  accept,
  'aria-label': ariaLabel,
  disabled,
  extraAnalyticsData,
}: Props) {
  const { user } = useUserEnvironment();
  const inputRef = useRef<HTMLInputElement | null | undefined>(null);
  const [urlPreview, setUrlPreview] = useState(null);
  const [filename, setFilename] = useState(null);
  const [created, setCreated] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [mouseActive, setMouseActive] = useState(false);

  const onMouseEnter = () => {
    setMouseActive(true);
  };

  const onMouseLeave = () => {
    setMouseActive(false);
  };

  const onDragEnter = () => {
    setDragActive(true);
    setMouseActive(true);
  };

  const onDragEnd = () => {
    setDragActive(false);
    setMouseActive(false);
  };

  const onClick = () => {
    if (!useMock && inputRef.current) {
      inputRef.current.click();
    }
  };

  const onFileChange = useCallback(() => {
    const fs = inputRef.current?.files;

    if (!fs || fs.length === 0) return;

    invariant(fs, 'fs must be defined'); // for flow

    const f = fs[0];
    setLoading(true);
    uploadToS3(f, fileType)
      .then((newFileId) => {
        // Track non-staff S3 uploads
        if (fileType !== 'CMS_UPLOAD') {
          let fileExtension;

          // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
          if (f && f.name) {
            fileExtension = f.name.split('.').pop();
          }

          trackEvent('Success: Uploaded File', {
            file_type: fileType,
            file_extension: fileExtension,
            user_type: getUserType(user),
            ...extraAnalyticsData,
          });
        }

        // @ts-expect-error TS(2345): Argument of type 'string' is not assignable to par... Remove this comment to see the full error message
        setFilename(f.name);
        // @ts-expect-error TS(2345): Argument of type 'Date' is not assignable to param... Remove this comment to see the full error message
        setCreated(new Date());
        // @ts-expect-error TS(2345): Argument of type 'string' is not assignable to par... Remove this comment to see the full error message
        setUrlPreview(URL.createObjectURL(f));
        onChange(newFileId);
      })
      .catch((e) => {
        if (onError) onError(e);
      })
      .finally(() => {
        setLoading(false);
        setDragActive(false);
        setMouseActive(false);
      });
  }, [user, extraAnalyticsData, fileType, onChange, onError]);

  useEffect(() => {
    if (fileId && !filename) {
      setLoading(true);
      getJson(`/data/s3-uploads/${fileId}/meta`)
        // @ts-expect-error TS(2345): Argument of type '({ s3File }: { s3File: any; }) =... Remove this comment to see the full error message
        .then(({ s3File }) => {
          setFilename(s3File.filename);
          setCreated(s3File.created);
        })
        .finally(() => {
          setLoading(false);
        });
    }

    if (fileId && !urlPreview) {
      // @ts-expect-error TS(2345): Argument of type 'string' is not assignable to par... Remove this comment to see the full error message
      setUrlPreview(fileIdToUrl(fileId, downloadAttachment));
    }
  }, [fileId, urlPreview, filename, downloadAttachment]);

  useEffect(() => {
    const input = inputRef.current;

    // If the file was removed outside the S3Picker, clear the input value
    if (!fileId && input) input.value = '';
  }, [fileId]);

  return (
    <InputContainer data-qa-id={qaId}>
      <input
        aria-label={ariaLabel}
        // @ts-expect-error TS(2322): Type 'MutableRefObject<HTMLInputElement | null | u... Remove this comment to see the full error message
        ref={inputRef}
        required={isRequired}
        name={name}
        type="file"
        onChange={onFileChange}
        disabled={disabled}
        data-qa-id={`${qaId}-hidden-input`}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onDragEnter={onDragEnter}
        onDragEnd={onDragEnd}
        onDragLeave={onDragEnd}
        onFocus={onMouseEnter}
        onBlur={onMouseLeave}
        accept={accept}
      />
      {children({
        onClick,
        urlPreview,
        filename,
        loading,
        dragActive: !disabled && dragActive,
        mouseActive: !disabled && mouseActive,
        created,
      })}
    </InputContainer>
  );
}
